const MONTH_SHORT_FR = [
  "janv.", "févr.", "mars", "avr.", "mai", "juin",
  "juil.", "août", "sept.", "oct.", "nov.", "déc."
]

export const TIMELINE_MONTHLY_MAX = 18
export const TIMELINE_TARGET_MIN = 10
export const TIMELINE_TARGET_MAX = 20

export function resolveTimelineGranularity(monthPoints) {
  const monthCount = monthPoints.length
  if (monthCount <= TIMELINE_MONTHLY_MAX) return "month"

  const yearCount = new Set(monthPoints.map((point) => point.year)).size
  const options = [
    { id: "month", count: monthCount },
    { id: "semester", count: Math.ceil(monthCount / 6) },
    { id: "quarter", count: Math.ceil(monthCount / 3) },
    { id: "year", count: yearCount }
  ]

  const viable = options.filter((option) => option.id !== "month" || option.count <= TIMELINE_TARGET_MAX)
  const inRange = viable.filter(
    (option) => option.count >= TIMELINE_TARGET_MIN && option.count <= TIMELINE_TARGET_MAX
  )

  if (inRange.length) {
    return inRange.reduce((best, option) => (option.count > best.count ? option : best)).id
  }

  return viable.reduce((best, option) => {
    const distance = timelineBucketDistance(option.count)
    const bestDistance = timelineBucketDistance(best.count)
    return distance < bestDistance ? option : best
  }).id
}

export function timelineBucketDistance(count) {
  if (count >= TIMELINE_TARGET_MIN && count <= TIMELINE_TARGET_MAX) return 0
  if (count < TIMELINE_TARGET_MIN) return TIMELINE_TARGET_MIN - count
  return count - TIMELINE_TARGET_MAX
}

export function timelineBucketKey(point, granularity) {
  if (granularity === "year") return String(point.year)

  if (granularity === "month") return `${point.year}-M${point.monthIndex}`

  if (granularity === "semester") {
    const semester = point.monthIndex < 6 ? 1 : 2
    return `${point.year}-S${semester}`
  }

  const quarter = Math.floor(point.monthIndex / 3) + 1
  return `${point.year}-Q${quarter}`
}

export function formatPeriodLabel(point, granularity, shortYear = true) {
  if (granularity === "year") return String(point.year)

  if (granularity === "month") return formatMonthCategory(point, shortYear)

  const yearLabel = shortYear ? String(point.year).slice(-2) : String(point.year)

  if (granularity === "semester") {
    const semester = point.monthIndex < 6 ? 1 : 2
    return `S${semester} ${yearLabel}`
  }

  const quarter = Math.floor(point.monthIndex / 3) + 1
  return `T${quarter} ${yearLabel}`
}

export function formatMonthCategory(point, shortYear = true) {
  if (point.periodLabel) return point.periodLabel

  const label = MONTH_SHORT_FR[point.monthIndex] || String(point.monthIndex + 1)
  if (!shortYear) return label
  return `${label} ${String(point.year).slice(-2)}`
}

export function sumNumeric(current, value) {
  if (value == null || Number.isNaN(value)) return current
  return (current || 0) + Number(value)
}

export function startOfDay(date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate())
}

export function dayCountInclusive(start, end) {
  const ms = startOfDay(end) - startOfDay(start)
  return Math.floor(ms / 86_400_000) + 1
}

export function periodBounds(bucket, granularity) {
  const year = Number(bucket.year)

  if (granularity === "year") {
    return {
      start: new Date(year, 0, 1),
      end: new Date(year, 11, 31)
    }
  }

  if (granularity === "semester") {
    const semester = bucket.semester ?? (bucket.monthIndex < 6 ? 1 : 2)
    const startMonth = semester === 1 ? 0 : 6
    return {
      start: new Date(year, startMonth, 1),
      end: new Date(year, startMonth + 6, 0)
    }
  }

  if (granularity === "quarter") {
    const quarter = bucket.quarter ?? Math.floor(bucket.monthIndex / 3) + 1
    const startMonth = (quarter - 1) * 3
    return {
      start: new Date(year, startMonth, 1),
      end: new Date(year, startMonth + 3, 0)
    }
  }

  const monthIndex = bucket.monthIndex ?? 0
  return {
    start: new Date(year, monthIndex, 1),
    end: new Date(year, monthIndex + 1, 0)
  }
}

export function currentPeriodProgress(bucket, granularity, referenceDate = new Date()) {
  const { start, end } = periodBounds(bucket, granularity)
  const today = startOfDay(referenceDate)
  const periodStart = startOfDay(start)
  const periodEnd = startOfDay(end)

  if (today < periodStart || today > periodEnd) return null

  const totalDays = dayCountInclusive(periodStart, periodEnd)
  const elapsedDays = dayCountInclusive(periodStart, today)

  if (elapsedDays >= totalDays) return null

  return { elapsedDays, totalDays }
}

export function linearPeriodProjection(value, progress) {
  if (value == null || Number.isNaN(value) || !progress) return null

  const { elapsedDays, totalDays } = progress
  if (elapsedDays <= 0 || totalDays <= elapsedDays) return null

  return Math.round(Number(value) * (totalDays / elapsedDays))
}

export function findInflightPeriodIndex(buckets, granularity, values, referenceDate = new Date()) {
  for (let index = buckets.length - 1; index >= 0; index -= 1) {
    const value = values[index]
    if (value == null || Number.isNaN(value)) continue
    if (currentPeriodProgress(buckets[index], granularity, referenceDate)) return index
  }

  return null
}

export function calendarMonthKey(year, monthIndex) {
  return `${year}-M${monthIndex}`
}

export function iterateCalendarMonths(fromYear, fromMonthIndex, toYear, toMonthIndex) {
  const buckets = []
  // Years may arrive as payload keys ("2025"): `"2025" + 1` would give "20251".
  let year = Number(fromYear)
  let monthIndex = Number(fromMonthIndex)
  toYear = Number(toYear)

  while (year < toYear || (year === toYear && monthIndex <= toMonthIndex)) {
    buckets.push({ year, monthIndex })
    monthIndex += 1
    if (monthIndex > 11) {
      monthIndex = 0
      year += 1
    }
  }

  return buckets
}

export function monthRangeEndingAt(referenceDate = new Date(), count = 12) {
  const endYear = referenceDate.getFullYear()
  const endMonthIndex = referenceDate.getMonth()

  let year = endYear
  let monthIndex = endMonthIndex - (count - 1)
  while (monthIndex < 0) {
    monthIndex += 12
    year -= 1
  }

  return iterateCalendarMonths(year, monthIndex, endYear, endMonthIndex)
}

export function monthRangeForYear(year, referenceDate = new Date()) {
  const yearNum = Number(year)
  const currentYear = referenceDate.getFullYear()
  const endMonthIndex = yearNum === currentYear ? referenceDate.getMonth() : 11

  return iterateCalendarMonths(yearNum, 0, yearNum, endMonthIndex)
}

export function monthRangeFromFirstDataToToday(dataBuckets, referenceDate = new Date()) {
  if (!dataBuckets?.length) return []

  const first = dataBuckets.reduce((earliest, bucket) => {
    if (bucket.year < earliest.year) return bucket
    if (bucket.year === earliest.year && bucket.monthIndex < earliest.monthIndex) return bucket
    return earliest
  }, dataBuckets[0])

  return iterateCalendarMonths(
    first.year,
    first.monthIndex,
    referenceDate.getFullYear(),
    referenceDate.getMonth()
  )
}

function flowSeriesValue(value) {
  if (value == null || Number.isNaN(value)) return 0
  return Number(value)
}

export function fillMultiFlowMonthBuckets(calendarMonths, dataMonths) {
  const dataIndex = new Map(
    (dataMonths || []).map((point) => [calendarMonthKey(point.year, point.monthIndex), point])
  )
  let lastActive = null

  return calendarMonths.map(({ year, monthIndex }) => {
    const data = dataIndex.get(calendarMonthKey(year, monthIndex))

    if (data?.active != null && !Number.isNaN(data.active)) {
      lastActive = Number(data.active)
    }

    return {
      year,
      monthIndex,
      subscriptions: flowSeriesValue(data?.subscriptions),
      terminations: flowSeriesValue(data?.terminations),
      imports: flowSeriesValue(data?.imports),
      active: lastActive ?? 0
    }
  })
}

export function fillMonthBuckets(calendarMonths, dataBuckets, { valueKey = "value" } = {}) {
  const dataIndex = new Map(
    (dataBuckets || []).map((bucket) => [calendarMonthKey(bucket.year, bucket.monthIndex), bucket])
  )

  return calendarMonths.map(({ year, monthIndex }) => {
    const data = dataIndex.get(calendarMonthKey(year, monthIndex))
    const rawValue = data?.[valueKey]
    const value = rawValue == null || Number.isNaN(rawValue) ? 0 : Number(rawValue)

    return { year, monthIndex, [valueKey]: value }
  })
}

export function timelinePointsToMonthBuckets(points, { valueKey = "value", getDate } = {}) {
  const monthPoints = (points || []).flatMap((point) => {
    const date = getDate ? getDate(point) : point?.date
    if (!date) return []

    const parsed = date instanceof Date ? date : new Date(date)
    if (Number.isNaN(parsed.getTime())) return []

    return [{
      year: parsed.getFullYear(),
      monthIndex: parsed.getMonth(),
      [valueKey]: point[valueKey]
    }]
  })

  return aggregateScalarMonthPoints(monthPoints, "month", { valueKey })
}

export function aggregateScalarMonthPoints(monthPoints, granularity, { valueKey = "value" } = {}) {
  const buckets = []
  const bucketIndex = new Map()

  monthPoints.forEach((point) => {
    const key = timelineBucketKey(point, granularity)
    let bucket = bucketIndex.get(key)

    if (!bucket) {
      bucket = {
        year: Number(point.year),
        monthIndex: point.monthIndex,
        quarter: granularity === "quarter" ? Math.floor(point.monthIndex / 3) + 1 : null,
        semester: granularity === "semester" ? (point.monthIndex < 6 ? 1 : 2) : null,
        periodLabel: formatPeriodLabel(point, granularity),
        value: null
      }
      bucketIndex.set(key, bucket)
      buckets.push(bucket)
    }

    bucket[valueKey] = sumNumeric(bucket[valueKey], point[valueKey])
    bucket.monthIndex = point.monthIndex
  })

  return buckets
}
